import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { RaisedButton, GhostButton } from '@hobs/components/Buttons';
import Icon from '@hobs/components/Icon';
import { H2 } from '@hobs/components/Headings';
import { Tree } from '@blueprintjs/core';
import withUnitDeletion from '@hobs/hocs/Units/withUnitDeletion/withUnitDeletion';

import { getPartyUnits } from '../../actions/parties';

import { deleteUnit, markUnitPendingForDeletion } from '../../actions/units';
import UnitDialog from './UnitDialog';
import './Units.scss';

class Units extends React.PureComponent {
  getUnitPathName(unitId = '') {
    return `${this.props.location.pathname.replace(
      '/unit',
      ''
    )}/unit/${unitId}`;
  }

  getUnitDeletePathName(unitId) {
    return `${this.props.location.pathname.replace(
      '/deleteunit',
      ''
    )}/deleteunit/${unitId}`;
  }

  controls(unit) {
    return (
      this.props.canEdit && (
        <span className="units-tree--controls">
          <GhostButton
            iconLeft={<Icon icon="pen" />}
            to={this.getUnitPathName(unit.unitId)}
          >
            {this.props.t('Edit')}
          </GhostButton>
          <GhostButton
            iconLeft={<Icon icon="delete" />}
            disabled={unit.isAssignedToActiveSnSor && unit.isDeletionPending}
            onClick={() => {
              this.props.onRemove(
                unit.unitId,
                () => this.props.markUnitPendingForDeletion(unit.unitId, true),
                () => this.props.deleteUnit(unit.unitId),
                this.reloadUnits
              );
            }}
          >
            {this.props.t('Delete')}
          </GhostButton>
          {unit.isDeletionPending && this.props.renderCancelPendingDeletionButton(
            unit,
            () => this.props.markUnitPendingForDeletion(unit.unitId, false),
            this.reloadUnits
          )}
        </span>
      )
    );
  }

  buildTree = (fn, [head, ...rest]) => {
    if (head === undefined) {
      return [];
    }
    if (head.units.length) {
      return [
        { ...fn(head), childNodes: this.buildTree(fn, head.units) },
        ...this.buildTree(fn, rest)
      ];
    }
    return [fn(head), ...this.buildTree(fn, rest)];
  };

  getUnitObject = unit => {
    return {
      ...unit,
      isExpanded: true,
      hasCaret: false,
      secondaryLabel: this.controls(unit),
      id: unit.unitId,
      key: unit.unitId,
      label: !unit.isDeletionPending ? unit.title : this.props.renderPendingDeletionUnitLabel(unit)
    };
  };

  reloadUnits = () => {
    this.props.getPartyUnits(this.props.partyId);
  };

  sortUnits(units) {
    if (!Array.isArray(units)) {
      return [];
    }

    const sortedUnits = units.sort((a, b) =>
      a.title.toLowerCase() > b.title.toLowerCase()
        ? 1
        : a.title.toLowerCase() < b.title.toLowerCase()
          ? -1
          : 0
    );

    return sortedUnits.map(unit => {
      return {
        ...unit,
        units: this.sortUnits(unit.units)
      };
    });
  }

  render() {
    const { t, partyId, partyUnits } = this.props;
    const sortedUnits = this.sortUnits(partyUnits);
    const unitsTree = this.buildTree(
      this.getUnitObject,
      this.props.partyUnits ? sortedUnits : []
    );
    return (
      <div className="units-view">
        <H2>{t('Manage structure')}</H2>
        <UnitDialog
          partyId={partyId}
          units={sortedUnits}
          reloadUnits={this.reloadUnits}
        />

        <div>
          <div className="units-view--header">
            <div className="units-view--left">
              <div>
                {this.props.canEdit && (
                  <RaisedButton
                    iconLeft={<Icon icon="plus" />}
                    to={this.getUnitPathName()}
                  >
                    {t('Add new element')}
                  </RaisedButton>
                )}
              </div>
            </div>
          </div>
          <div>
            {unitsTree.length > 0 && (
              <Tree
                className="units-tree"
                contents={unitsTree}
                onNodeCollapse={this.handleNodeCollapse}
                onNodeExpand={this.handleNodeExpand}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

Units.propTypes = {
  //withUnitDeletion hoc
  onRemove: PropTypes.func,
  renderCancelPendingDeletionButton: PropTypes.func,
  renderPendingDeletionUnitLabel: PropTypes.func,
  //other
  location: PropTypes.object,
  match: PropTypes.object,
  history: PropTypes.object,
  t: PropTypes.func,
  partyUnits: PropTypes.array,
  partyId: PropTypes.string,
  getPartyUnits: PropTypes.func,
  deleteUnit: PropTypes.func,
  canEdit: PropTypes.bool,
  markUnitPendingForDeletion: PropTypes.bool,
};

const mapStateToProps = state => ({
  partyUnits: state.parties.partyUnits,
  deleteUnit: PropTypes.func
});

const mapDispatchToProps = {
  getPartyUnits,
  deleteUnit,
  markUnitPendingForDeletion
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(withUnitDeletion(Units)));
